import { useEffect, useRef, useState } from 'react';
import { Modal, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';

interface PickupSignatureModalItem {
  id: string;
  name: string;
  quantity?: number;
  unitOfMeasure?: string;
}

interface PickupSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (signatureData: string, notes: string) => void;
  orderNumber?: string;
  items: PickupSignatureModalItem[];
  isSubmitting?: boolean;
}

export function PickupSignatureModal({
  isOpen,
  onClose,
  onConfirm,
  orderNumber,
  items,
  isSubmitting = false,
}: PickupSignatureModalProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState('');
  const [signerName, setSignerName] = useState('');

  // Reset state whenever the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setHasSignature(false);
      setNotes('');
      setError('');
      setSignatureMode('draw');
      setTypedName('');
      setSignerName('');
      // Clear canvas on open (deferred so the canvas is mounted/sized first)
      requestAnimationFrame(() => clearCanvas());
    }
  }, [isOpen]);

  const getCanvasContext = () => canvasRef.current?.getContext('2d') || null;

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = getCanvasContext();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getPoint = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
    if (clientX === undefined || clientY === undefined) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (isSubmitting) return;
    e.preventDefault();
    const point = getPoint(e);
    if (!point) return;
    isDrawingRef.current = true;
    lastPointRef.current = point;
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current || isSubmitting) return;
    e.preventDefault();
    const ctx = getCanvasContext();
    const point = getPoint(e);
    if (!ctx || !point || !lastPointRef.current) return;

    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPointRef.current = point;
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  };

  // Renders a typed name onto an offscreen canvas so the output stays a PNG data URL,
  // matching the format the backend already expects from drawn signatures.
  const renderTypedSignature = (name: string): string | null => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111827';
    ctx.font = "48px 'Brush Script MT', cursive";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, canvas.height / 2, canvas.width - 20);
    return canvas.toDataURL('image/png');
  };

  // Stamps the printed name beneath a drawn signature so the stored image is self-explanatory
  // about who signed, since a hand-drawn signature alone is often illegible.
  const composeDrawnSignatureWithName = (name: string): string | null => {
    const source = canvasRef.current;
    if (!source) return null;
    const nameBandHeight = 32;
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height + nameBandHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0);
    ctx.fillStyle = '#111827';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, source.height + nameBandHeight / 2, canvas.width - 20);
    return canvas.toDataURL('image/png');
  };

  const switchSignatureMode = (mode: 'draw' | 'type') => {
    if (isSubmitting || mode === signatureMode) return;
    setSignatureMode(mode);
    setError('');
  };

  const handleClear = () => {
    if (signatureMode === 'type') {
      setTypedName('');
    } else {
      clearCanvas();
      setSignerName('');
    }
    setError('');
  };

  const handleConfirm = () => {
    if (signatureMode === 'type') {
      const trimmedName = typedName.trim();
      if (!trimmedName) {
        setError('Please type your name to confirm pickup.');
        return;
      }
      const signatureData = renderTypedSignature(trimmedName);
      if (!signatureData) return;
      onConfirm(signatureData, notes.trim());
      return;
    }
    if (!hasSignature) {
      setError('Please sign in the box below to confirm pickup.');
      return;
    }
    const trimmedSignerName = signerName.trim();
    if (!trimmedSignerName) {
      setError('Please type your name for clarity of who signed.');
      return;
    }
    const signatureData = composeDrawnSignatureWithName(trimmedSignerName);
    if (!signatureData) return;
    onConfirm(signatureData, notes.trim());
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Confirm Pickup"
      size="sm"
      data-testid="pickup-signature-modal"
    >
      <div className="space-y-4">
        {orderNumber && (
          <p className="text-sm text-gray-600 dark:text-secondary-400">Order #{orderNumber}</p>
        )}

        {items.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-secondary-400 mb-2">
              Items Handed Off
            </h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-md bg-gray-50 dark:bg-secondary-900 px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400">
                      <i className="fa-solid fa-check text-xs"></i>
                    </span>
                    <span className="font-medium text-gray-800 dark:text-secondary-100">{item.name}</span>
                  </div>
                  {item.quantity != null && (
                    <span className="text-gray-500 dark:text-secondary-400">
                      {item.quantity}{item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200">
              Signature <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <button
              type="button"
              onClick={handleClear}
              disabled={isSubmitting}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
            >
              Clear
            </button>
          </div>

          <div className="flex gap-1 mb-2">
            <button
              type="button"
              onClick={() => switchSignatureMode('draw')}
              disabled={isSubmitting}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50 ${
                signatureMode === 'draw'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-300'
              }`}
              data-testid="pickup-signature-mode-draw"
            >
              <i className="fa-solid fa-pen mr-1"></i>
              Draw
            </button>
            <button
              type="button"
              onClick={() => switchSignatureMode('type')}
              disabled={isSubmitting}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50 ${
                signatureMode === 'type'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-300'
              }`}
              data-testid="pickup-signature-mode-type"
            >
              <i className="fa-solid fa-keyboard mr-1"></i>
              Type
            </button>
          </div>

          {signatureMode === 'draw' ? (
            <div>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                disabled={isSubmitting}
                placeholder="Print your full name"
                className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                data-testid="pickup-signature-signer-name-input"
              />
              <div
                className={`relative rounded-md border-2 border-dashed ${
                  error ? 'border-red-400' : 'border-gray-300 dark:border-secondary-600'
                } bg-gray-50 dark:bg-secondary-900`}
              >
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={140}
                  className="w-full touch-none rounded-md bg-white dark:bg-secondary-800"
                  style={{ height: 140 }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="pickup-signature-canvas"
                />
                {!hasSignature && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400 dark:text-secondary-500">
                    Click &amp; drag to sign
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={isSubmitting}
                placeholder="Type your full name"
                className="w-full mb-2 px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                data-testid="pickup-signature-typed-input"
              />
              <div
                className={`flex items-center justify-center rounded-md border-2 border-dashed ${
                  error ? 'border-red-400' : 'border-gray-300 dark:border-secondary-600'
                } bg-white dark:bg-secondary-800`}
                style={{ height: 140 }}
              >
                {typedName.trim() ? (
                  <span
                    className="px-4 text-3xl text-gray-800 dark:text-secondary-100"
                    style={{ fontFamily: "'Brush Script MT', cursive" }}
                    data-testid="pickup-signature-typed-preview"
                  >
                    {typedName}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400 dark:text-secondary-500">
                    Your signature preview will appear here
                  </span>
                )}
              </div>
            </div>
          )}
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div>
          <label htmlFor="pickupNotes" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
            Pickup Notes (optional)
          </label>
          <textarea
            id="pickupNotes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
            placeholder="e.g. Shopper arrived early, all items verified..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />
        </div>
      </div>

      <ModalFooter className="px-0 pt-4 pb-0 border-t-0 bg-transparent">
        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          className="bg-green-600 hover:bg-green-700"
          onClick={handleConfirm}
          disabled={isSubmitting}
          data-testid="pickup-signature-confirm-btn"
        >
          {isSubmitting ? 'Processing...' : (
            <>
              <i className="fa-solid fa-check mr-2"></i>
              Confirm Pickup
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
